import { WarningFilled } from '@ant-design/icons';
import { EnumRichiestaDTOStato } from '@framework/api-client/api/inquiries-compile/schemas/enumRichiestaDTOStato.ts';
import { EnumRichiestaDTOTipoIntegrazione } from '@framework/api-client/api/inquiries-compile/schemas/enumRichiestaDTOTipoIntegrazione.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type RichiestaDTOStato } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTOStato.ts';
import { expectNeverAsString } from '@framework/engine/utils/expectNeverAsString.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { StyledTag } from '@framework/ui/components/tag/StyledTag.tsx';
import { type TagProps, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import styled, { type DefaultTheme, useTheme } from 'styled-components';
import { SubmissionBurdenLogic } from '../../burden/SubmissionBurdenLogic.ts';
import { useQuoteActionRequiredMessages } from '../../payments/quote/QuoteActionRequiredMessages.tsx';

const SidedStyledTag = styled(StyledTag)<{ $side?: 'left' | 'right' }>`
  border-top-right-radius: ${props => (props.$side === 'left' ? '0' : '2px')};
  border-bottom-right-radius: ${props =>
    props.$side === 'left' ? '0' : '2px'};
  border-top-left-radius: ${props => (props.$side === 'right' ? '0' : '2px')};
  border-bottom-left-radius: ${props =>
    props.$side === 'right' ? '0' : '2px'};

  margin-inline-end: ${props => (props.$side == 'left' ? '0' : '8px')};
`;

/**
 * Ritorna le impostazioni per il tag
 *
 */
function getTagOptions(
  state: RichiestaDTOStato | undefined,
  theme?: DefaultTheme,
): TagProps {
  if (!state) return { color: '#a5c201', children: 'N/D' };
  switch (state) {
    case EnumRichiestaDTOStato.BOZZA:
      return {
        color: '#757575',
        children: t('submission-state-tag.bozza', 'BOZZA'),
      };
    case EnumRichiestaDTOStato.PAGAMENTO:
      return {
        color: '#feaf2d',
        children: t('submission-state-tag.pagamento', 'IN PAGAMENTO'),
      };
    case EnumRichiestaDTOStato.CREATA:
      return {
        color: '#9254de',
        children: t('submission-state-tag.creata', 'CREATA'),
      };
    case EnumRichiestaDTOStato.IN_INVIO:
      return {
        color: theme?.colorInfo ?? '#569bc4',
        children: t('submission-state-tag.in-invio', 'IN INVIO'),
      };
    case EnumRichiestaDTOStato.INVIATA:
      return {
        color: theme?.colorSuccess ?? '#389e0d',
        children: t('submission-state-tag.inviata', 'INVIATA'),
      };
    case EnumRichiestaDTOStato.INTEGRATA:
      return {
        color: '#4b4f4d',
        children: t('submission-state-tag.integrata', 'INTEGRATA'),
      };
    case EnumRichiestaDTOStato.CANCELLATA:
      return {
        color: theme?.colorError ?? '#fe0000',
        children: t('submission-state-tag.cancellata', 'CANCELLATA'),
      };
    case EnumRichiestaDTOStato.RICEVUTA:
      return {
        color: theme?.colorSuccess ?? '#389e0d',
        children: t('submission-state-tag.ricevuta', 'RICEVUTA'),
      };
    case EnumRichiestaDTOStato.CONCLUSA:
      return {
        color: theme?.colorSuccessActive ?? '#227100',
        children: t('submission-state-tag.conclusa', 'CONCLUSA'),
      };
    case EnumRichiestaDTOStato.ANNULLATA:
      return {
        color: theme?.colorError ?? '#fe0000',
        children: t('submission-state-tag.annullata', 'ANNULLATA'),
      };
    case EnumRichiestaDTOStato.ANNULLAMENTO_IN_CORSO:
      return {
        color: theme?.colorWarningActive ?? '#9E6C00',
        children: t(
          'submission-state-tag.annullamento-in-corso',
          'ANNULLAMENTO IN CORSO',
        ),
      };
    default:
      return { children: expectNeverAsString(state) };
  }
}

export interface SubmissionStateTagProps {
  submission?: RichiestaDTO | null;
  mini?: boolean;
}

/**
 * Stato di un Tipo Domanda
 */
export function SubmissionStateTag(props: SubmissionStateTagProps) {
  const { submission, mini } = props;
  const state = submission?.stato;
  const theme = useTheme();
  const { t } = useTranslation();

  const hasPendingQuotesTooltipText =
    useQuoteActionRequiredMessages(submission);

  if (!state) return null;

  const needIntegration =
    submission?.tipoIntegrazione ===
    EnumRichiestaDTOTipoIntegrazione.RICHIESTA_INTEGRAZIONE;

  const hasPendingQuotes = SubmissionBurdenLogic.hasPendingQuotes(
    submission.statoPreventivi,
  );

  const isAlreadyIntegrated = state === EnumRichiestaDTOStato.INTEGRATA;

  const needIntegrationTooltipText = isAlreadyIntegrated
    ? t(
        'submission-state-tag.integrata-dopo-richiesta-integrazione',
        'Integrata dopo richiesta di integrazione',
      )
    : t(
        'submission-state-tag.richiesta-integrazione',
        "È richiesta l'integrazione",
      );

  return (
    <span style={{ textWrap: 'nowrap' }}>
      <SidedStyledTag
        mini={mini}
        $side={needIntegration || hasPendingQuotes ? 'left' : undefined}
        {...getTagOptions(state, theme)}
      />
      {needIntegration && (
        <Tooltip title={needIntegrationTooltipText}>
          <SidedStyledTag
            mini={mini}
            $side="right"
            color={theme?.colorWarning ?? '#faad14'}
          >
            <WarningFilled aria-label={needIntegrationTooltipText} />
          </SidedStyledTag>
        </Tooltip>
      )}
      {hasPendingQuotes && (
        <Tooltip title={hasPendingQuotesTooltipText}>
          <SidedStyledTag
            mini={mini}
            $side="right"
            color={theme?.colorWarning ?? '#faad14'}
          >
            <WarningFilled aria-label={hasPendingQuotesTooltipText} />
          </SidedStyledTag>
        </Tooltip>
      )}
    </span>
  );
}
